import { useState, useRef } from 'react';
import api, { API, formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { ImagePlus, Trash2, Eye, EyeOff, Loader2 } from 'lucide-react';

export const photoUrl = (storagePath) => `${API}/photos/${storagePath}`;

export default function PhotoAttachments({ projectId, initialPhotos = [] }) {
  const [photos, setPhotos] = useState(initialPhotos || []);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setUploading(true);
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append('file', f);
        const { data } = await api.post(`/projects/${projectId}/photos`, fd);
        setPhotos((p) => [...p, data]);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || 'Upload failed');
      }
    }
    setUploading(false);
  };

  const toggle = async (ph) => {
    const visible = !ph.visible;
    setPhotos((p) => p.map((x) => (x.id === ph.id ? { ...x, visible } : x)));
    try {
      await api.patch(`/projects/${projectId}/photos/${ph.id}`, { visible });
    } catch {
      toast.error('Could not update photo');
      setPhotos((p) => p.map((x) => (x.id === ph.id ? { ...x, visible: ph.visible } : x)));
    }
  };

  const remove = async (ph) => {
    const prev = photos;
    setPhotos((p) => p.filter((x) => x.id !== ph.id));
    try {
      await api.delete(`/projects/${projectId}/photos/${ph.id}`);
    } catch {
      toast.error('Could not delete photo');
      setPhotos(prev);
    }
  };

  return (
    <div className="tm-card p-5" data-testid="photo-attachments">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-widest text-brand font-semibold">Site photos</p>
          <h3 className="font-display font-bold uppercase tracking-tight">Photos of the work area</h3>
          <p className="text-slate-600 text-sm mt-1">Attach photos of where the work will be done. Use the eye toggle to show or hide each one on the customer's quote.</p>
        </div>
        <button
          type="button"
          data-testid="photo-upload-btn"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="tm-btn inline-flex items-center gap-2 shrink-0"
        >
          {uploading ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />}
          {uploading ? 'Uploading…' : 'Add photos'}
        </button>
        <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={onPick} data-testid="photo-file-input" />
      </div>

      {photos.length === 0 ? (
        <p className="text-slate-400 text-sm mt-4 italic">No photos yet — add JPG, PNG or HEIC images (max 8MB each).</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mt-4">
          {photos.map((ph) => (
            <div key={ph.id} className="relative group rounded-lg overflow-hidden border border-slate-200" data-testid={`photo-${ph.id}`}>
              <img src={photoUrl(ph.storage_path)} alt={ph.caption || ph.filename} className="w-full h-28 object-cover" loading="lazy" />
              <div className={`absolute top-1 left-1 px-1.5 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider ${ph.visible ? 'bg-brand text-slate-900' : 'bg-slate-800 text-white'}`}>
                {ph.visible ? 'Shown' : 'Hidden'}
              </div>
              <div className="absolute bottom-1 right-1 flex gap-1">
                <button
                  type="button"
                  onClick={() => toggle(ph)}
                  data-testid={`photo-toggle-${ph.id}`}
                  title={ph.visible ? 'Hide from customer' : 'Show to customer'}
                  className="p-1.5 rounded bg-white/90 hover:bg-white text-slate-800 shadow"
                >
                  {ph.visible ? <Eye size={14} /> : <EyeOff size={14} />}
                </button>
                <button
                  type="button"
                  onClick={() => remove(ph)}
                  data-testid={`photo-delete-${ph.id}`}
                  title="Delete photo"
                  className="p-1.5 rounded bg-white/90 hover:bg-white text-red-600 shadow"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
